import { api } from "@/lib/api";

/**
 * swingLookup — shared, TTL-cached lookup for "is this inspection part of a
 * CLOSED swing?" so the InspectionDetail drawer can force read-only whenever
 * a supervisor drills back into a finished shift.
 *
 * Josh (Jul 10) locked the rule: "Past Swings access should be VIEW-ONLY.
 * No re-opening or editing once a shift is ended." The inspection's own
 * status (submitted/in-progress/planned) isn't enough — a 90 %-done
 * inspection would still look editable via its own state alone. Bucketing
 * the check off the parent swing's `status="closed"` catches all of them.
 *
 * Cache is module-level with a 10 s TTL and de-dupes concurrent callers via
 * a single in-flight promise so multiple simultaneous InspectionDetail
 * mounts don't hammer /swings.
 */
const TTL_MS = 10_000;
let _cache = null; // { at, closedInspectionIds }
let _inFlight = null;

export async function fetchClosedInspectionSet() {
  if (_cache && Date.now() - _cache.at < TTL_MS) {
    return _cache.closedInspectionIds;
  }
  if (_inFlight) return _inFlight;
  _inFlight = (async () => {
    try {
      const { data } = await api.get("/swings", { params: { limit: 200 } });
      const list = Array.isArray(data) ? data : (data?.items || data?.results || []);
      const set = new Set();
      list.forEach((s) => {
        if (String(s.status || "").toLowerCase() !== "closed") return;
        (s.inspection_ids || []).forEach((id) => set.add(String(id)));
      });
      _cache = { at: Date.now(), closedInspectionIds: set };
      return set;
    } catch {
      // On failure, keep whatever we had; return empty so we don't
      // accidentally lock everything down when the API blips.
      return _cache?.closedInspectionIds || new Set();
    } finally {
      _inFlight = null;
    }
  })();
  return _inFlight;
}

/** Force the next call to re-hit the network. */
export function invalidateClosedInspectionSet() { _cache = null; }
